<template>
  <div class="movie-style-container">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="search-controls">
        <el-input 
          v-model="searchKeyword" 
          placeholder="搜索角色..." 
          class="search-input"
          @keyup.enter.native="handleSearch">
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
        
        <!-- 作品下拉框 -->
        <div class="filter-controls">
          <el-select 
            v-model="workId" 
            placeholder="选择作品" 
            @change="handleSearch"
            filterable>
            <el-option label="所有作品" value=""></el-option>
            <el-option 
              v-for="work in allWorks" 
              :key="work.id" 
              :label="work.name" 
              :value="work.id">
            </el-option>
          </el-select>
        </div>
        
        <el-button type="primary" @click="addRole">添加角色</el-button>
      </div>
    </div>
    
    <!-- 角色网格 -->
    <div class="roles-grid">
      <div class="role-card" v-for="role in roleList" :key="role.id">
        <div class="card-inner">
          <div class="poster-container">
            <img 
              :src="role.imageUrl || defaultRoleImage" 
              class="poster" 
              alt="角色图片">
            <div class="overlay">
              <div class="actions">
                <el-button type="text" icon="el-icon-edit" @click="manageTags(role)">编辑标签</el-button>
                <el-button type="text" icon="el-icon-edit" @click="editRole(role)">编辑</el-button>
                <el-button type="text" icon="el-icon-delete" @click="deleteRole(role.id)">删除</el-button>
              </div>
            </div>
          </div>
          
          <div class="role-info">
            <h3 class="title">{{ role.name }}</h3>
            <p class="work-name">{{ role.workName || '未知作品' }}</p>
            
            <div class="tags-container">
              <span 
                v-for="tag in role.tags" 
                :key="tag.id" 
                class="tag-item"
                @click="copyTagContent(tag.content)">
                {{ tag.content }}
              </span>
              <span v-if="!role.tags || role.tags.length === 0" class="empty-tag">无标签</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 分页 -->
    <div v-if="total > 0 && !loading" class="pagination">
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange">
      </el-pagination>
    </div>

    <!-- 优化后的标签编辑弹窗 -->
    <el-dialog 
      title="编辑角色标签" 
      :visible.sync="tagDialogVisible"
      width="550px"
      custom-class="tag-dialog">
      
      <div class="tag-edit-container">
        <!-- 添加标签区域 -->
        <div class="card-section add-tag-section">
          <h3 class="section-title">添加新标签</h3>
          <el-input
            v-model="newTagName"
            placeholder="输入标签名称（例如：主角、反派）"
            class="tag-input"
            @keyup.enter.native="handleAddTag">
            <el-button 
              slot="append" 
              icon="el-icon-plus" 
              type="primary"
              @click="handleAddTag"
              :loading="addingTag">
              添加标签
            </el-button>
          </el-input>
          <p class="section-desc">添加常用标签，方便后续快速选择</p>
        </div>
        
        <!-- 当前标签区域 -->
        <div class="card-section current-tags-section">
          <h3 class="section-title">当前标签</h3>
          
          <div class="tags-list">
            <transition-group name="tag-item">
              <el-tag
                v-for="tag in currentRole.tags"
                :key="tag.id"
                closable
                @close="removeTag(tag.id)"
                class="custom-tag">
                <span class="tag-content">{{ tag.content }}</span>
              </el-tag>
            </transition-group>
            
            <!-- 优化的空状态 -->
            <div v-if="currentRole.tags.length === 0" class="empty-state">
              <div class="empty-icon">🏷️</div>
              <p class="empty-text">暂无标签</p>
              <p class="empty-desc">添加标签可以更好地对角色进行分类和管理</p>
            </div>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button 
          @click="tagDialogVisible = false"
          class="cancel-btn">
          取消
        </el-button>
        <el-button 
          type="primary" 
          @click="saveTags"
          :loading="savingTags"
          class="save-btn">
          保存标签
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getRoles, deleteRole, searchRoles } from '@/api/role';
import { getAllWorks } from '@/api/work';
import { updateRoleTags, createTag } from '@/api/tag';

export default {
  name: 'RoleListView',
  computed: {
    // 过滤作品列表
    filteredWorks() {
      return (this.allWorks || []).filter(work => work && work.id && work.name);
    },
    // 过滤角色列表
    filteredRoles() {
      return (this.roleList || []).filter(role => role && role.id && role.name);
    },
    // 过滤当前角色标签
    filteredTags() {
      return (this.currentRole.tags || []).filter(tag => tag && tag.id && tag.content);
    },
    // 过滤所有标签
    filteredAllTags() {
      return (this.allTags || []).filter(tag => tag && tag.id && tag.content);
    },
    // 判断是否有标签
    hasTags() {
      return this.filteredTags.length > 0;
    }
  },
  data() {
    return {
      roleList: [],
      allWorks: [],
      allTags: [],
      searchKeyword: '',
      workId: '',
      page: 1,
      pageSize: 12,
      total: 0,
      loading: false,
      errorMessage: '',
      defaultRoleImage: require('@/assets/default-role.png'),
      // 弹窗相关状态
      tagDialogVisible: false,
      currentRole: { tags: [] },
      selectedTagIds: [],
      newTagName: '',
      addingTag: false,
      savingTags: false,
      workLoading: false // 作品加载状态
    };
  },

  created() {
    this.workId = this.$route.query.workId || '';
    this.loadRoleList();
    this.loadAllWorks();
    const workIdParam = this.$route.query.workId;
    if (workIdParam) {
      this.workId = Number(workIdParam); // 转换为数字类型
    }
  },
  
  methods: {
    async loadRoleList() {
      this.loading = true;
      try {  

        let response;

        if (this.searchKeyword) {
          response = await searchRoles({ 
            keyword: this.searchKeyword,
            page: this.page,
            size: this.pageSize
             });
        } else {
          response = await getRoles({
            page: this.page,
            size: this.pageSize,
            workId: this.workId || undefined
          });
        }
        
        // 输出完整响应数据结构
        console.log('完整响应数据:', response);
        
        // 修复数据解析路径 - 假设后端数据在response.data.list中
        const rawData = response.data.data.rolesList || {};
        const rolesFromBackend = Array.isArray(rawData) ? rawData : [];

        this.total = response.data.data.total;
        
        // 简化标签处理逻辑
        this.roleList = rolesFromBackend.map(role => {
          // 暂时移除复杂的标签处理，确保角色数据能显示
          return {
            id: role.id,
            name: role.name,
            workName: role.workName,
            imageUrl: role.imageUrl,
            tagIds: role.tagIds,
            tagContents: role.tagContents,
            tags: [] // 暂时清空标签，确保角色能显示
          };
        });

        // 处理标签数据
        this.roleList.forEach(role => {
          if (role.tagIds && role.tagContents) {
            const tagIds = role.tagIds.split(',').map(id => parseInt(id));
            const tagContents = role.tagContents.split(',');
            
            role.tags = tagIds.map((id, index) => ({
              id,
              content: tagContents[index] || '未命名标签'
            }));
          } else {
            role.tags = [];
          }
        });
        
      } catch (error) {
        console.error('加载角色失败:', error);
        this.errorMessage = '加载角色失败: ' + error.message;
        this.roleList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // 简化搜索方法确保可执行
    handleSearch() {
      console.log('搜索按钮点击，关键词:', this.searchKeyword);
      this.page = 1;
      this.loadRoleList();
    },

    // 简化清除方法确保可执行
    clearSearch() {
      console.log('清除搜索按钮点击');
      this.searchKeyword = '';
      
      if (!this.loading) {
        this.page = 1;
        this.loadRoleList();
      }
    },
    
    handlePageChange(page) {
      this.page = page;
      this.loadRoleList();
    },
    
    handleSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.loadRoleList();
    },
    
    addRole() {
      this.$router.push({ path: '/role-form', query: { workId: this.workId } });
    },
    
    // 修复编辑页面404问题
    editRole(role) {
      // 确保路由路径正确
      this.$router.push({ 
        path: '/role-form', // 确保路由定义中存在此路径
        query: { id: role.id, workId: this.workId }
      });
    },
    
    async deleteRole(id) {
      this.$confirm('确定要删除这个角色吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 显示加载状态
          this.loading = true;
          await deleteRole(id);
          this.$message.success('删除成功');
          // 删除成功后刷新角色列表
          this.loadRoleList();
        } catch (error) {
          this.$message.error('删除失败: ' + error.message);
        } finally {
          // 隐藏加载状态
          this.loading = false;
        }
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    
    // 修复编辑标签弹窗不显示问题
    manageTags(role) {
      try {
        // 验证角色数据
        if (!role || typeof role !== 'object' || !role.id) {
          this.$message.error('无效的角色数据');
          return;
        }
        
        // 安全复制角色数据
        this.currentRole = {
          id: role.id,
          tags: Array.isArray(role.tags) ? [...role.tags] : []
        };
        
        // 提取标签ID
        this.selectedTagIds = this.currentRole.tags.map(tag => tag.id).filter(id => typeof id === 'number');
        
        // 显示弹窗
        this.tagDialogVisible = true;
        console.log('编辑标签弹窗已显示', this.currentRole);
      } catch (error) {
        console.error('编辑标签错误:', error);
        this.$message.error('打开标签编辑失败: ' + error.message);
        this.tagDialogVisible = false;
      }
    },
    
    async handleAddTag() {
      if (this.addingTag) return;
      
      const tagName = this.newTagName.trim();
      if (!tagName) {
        this.errorMessage = '标签名称不能为空';
        return;
      }
      
      this.addingTag = true;
      
      try {
        console.log('开始创建标签:', tagName);
        
        // 1. 调用创建标签API
        const createResponse = await createTag(tagName);
        console.log('创建标签API响应:', createResponse);
        
        const newTag = createResponse.data.data;
        if (!newTag || !newTag.id) {
          throw new Error('创建标签失败: 未返回有效标签ID');
        }
        
        console.log('创建新标签成功:', newTag);
        
        // 2. 更新角色标签关联
        this.currentRole.tags.push(newTag);
        this.selectedTagIds.push(newTag.id);
        
        // 3. 调用API保存标签关联
        console.log('保存角色标签关联:', this.currentRole.id, this.selectedTagIds);
        await updateRoleTags(this.currentRole.id, this.selectedTagIds);
        console.log('标签关联保存成功');
        
        this.newTagName = '';
        this.$message.success('标签添加成功');
      } catch (error) {
        console.error('添加标签完整错误:', error);
        this.errorMessage = `添加标签失败: ${error.message || '未知错误'}`;
      } finally {
        this.addingTag = false;
      }
    },
    
    async saveTags() {
      try {
        this.savingTags = true; // 保存开始，设置为true
        if (!this.currentRole || !this.currentRole.id) {
          throw new Error('角色ID缺失');
        }
        
        console.log('保存标签前状态:', {
          selectedTagIds: this.selectedTagIds,
          allTags: this.allTags.map(t => ({id: t.id, content: t.content}))
        });
        
        // 调用API保存标签关联
        await updateRoleTags(this.currentRole.id, this.selectedTagIds);
        
        // 修复标签重建逻辑
        let updatedTags = [];
        if (this.selectedTagIds.length > 0 && this.allTags.length > 0) {
          // 从allTags中筛选选中的标签
          updatedTags = this.allTags
            .filter(tag => this.selectedTagIds.includes(tag.id))
            .map(tag => ({ id: tag.id, content: tag.content }));
          console.log('基于allTags筛选的标签:', updatedTags);
        } else if (this.currentRole.tags.length > 0) {
          // 降级方案：使用当前角色的标签
          updatedTags = [...this.currentRole.tags];
          console.log('使用当前角色的标签:', updatedTags);
        }
        
        console.log('最终更新的标签列表:', updatedTags);
        
        // 完全替换标签数组
        this.currentRole.tags = [...updatedTags];
        
        // 更新本地角色列表
        const roleIndex = this.roleList.findIndex(r => r.id === this.currentRole.id);
        if (roleIndex !== -1) {
          this.roleList[roleIndex].tags = [...updatedTags];
          console.log('本地角色标签已更新:', this.roleList[roleIndex].tags);
        }
        
        this.tagDialogVisible = false;
        this.$message.success('标签更新成功');
      } catch (error) {
        console.error('保存标签失败:', error);
        this.errorMessage = '保存标签失败: ' + error.message;
      } finally{
        this.savingTags = false; // 保存结束，恢复为false
      }
    },

    handleTagSelect() {
      this.currentRole.tags = this.filteredAllTags.filter(tag => 
        this.selectedTagIds.includes(tag.id.toString())
      );
    },

    // 移除标签 - 确保同步更新selectedTagIds
    removeTag(tagId) {
      // 从当前角色标签中移除
      this.currentRole.tags = this.currentRole.tags.filter(tag => tag.id !== tagId);
      // 同步更新selectedTagIds
      this.selectedTagIds = this.selectedTagIds.filter(id => id !== tagId);
      console.log('移除标签后状态:', {
        remainingTags: this.currentRole.tags.map(t => t.id),
        selectedTagIds: this.selectedTagIds
      });
    },

    // 修复标签复制功能的提示逻辑
    copyTagContent(content) {
      try {
        if (!content) {
          this.$message.warning('标签内容为空，无法复制');
          return;
        }
        
        // 创建临时文本区域
        const textarea = document.createElement('textarea');
        textarea.value = content;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        
        // 使用成功提示，不设置errorMessage
        this.$message.success(`标签内容已复制: ${content}`);
        console.log('标签内容复制成功:', content);
      } catch (error) {
        console.error('复制标签失败:', error);
        this.$message.error(`复制失败: ${error.message || '未知错误'}`);
        // 仅在真正失败时设置errorMessage
        this.errorMessage = '复制标签失败: ' + error.message;
      }
    },

    async loadAllWorks() {
      try {
        const response = await getAllWorks();
        this.allWorks = response.data?.data || [];
      } catch (error) {
        console.error('加载作品列表失败:', error);
        this.errorMessage = '加载作品失败: ' + error.message;
      }
    }

  },
  
  watch: {
    '$route.query.workId'(newVal) {
      this.workId = newVal || '';
      this.page = 1;
      this.loadRoleList();
    }
  }
};
</script>

<style scoped>
.movie-style-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 弹窗整体样式 */
.tag-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.tag-dialog .el-dialog__header {
  background-color: #f5f7fa;
  padding: 18px 24px;
  border-bottom: 1px solid #e4e7ed;
}

.tag-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

.tag-dialog .el-dialog__body {
  padding: 24px;
}

.tag-dialog .el-dialog__footer {
  padding: 16px 24px;
  border-top: 1px solid #e4e7ed;
  background-color: #f5f7fa;
}

/* 卡片区域样式 */
.card-section {
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.card-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
}

.search-controls {
  display: flex;
  gap: 15px;
  align-items: center;
}

.search-input {
  width: 280px; /* 设置固定宽度 */
  max-width: 100%;
}

.filter-controls {
  width: 200px;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 25px;
}

.role-card {
  transition: transform 0.3s;
}

.role-card:hover {
  transform: translateY(-5px);
}

.card-inner {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.poster-container {
  position: relative;
  padding-top: 140%;
}

.poster {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 作品管理风格的按钮样式（带文字的矩形按钮） */
.actions {
  width: 100%;
  padding: 5px;
  display: flex;
  justify-content: space-around;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
  z-index: 10; /* 确保在最上层 */
}

.actions .el-button {
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  align-items: flex-end;
}

.btn-action {
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  z-index: 20; /* 确保按钮在最上层 */
  position: relative;
}

.role-card {
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100%;
}

.role-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
}

.role-card:hover .overlay {
  opacity: 1;
}

.btn-action i {
  font-size: 14px;
}

.btn-action:hover {
  background-color: #409eff;
}

.role-info {
  padding: 15px;
}

.title {
  font-size: 16px;
  margin: 0 0 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.work-name {
  color: #889099;
  margin: 0 0 10px 0;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.tag-item {
  background-color: #e4f0ff;
  color: #409eff;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.empty-tag {
  color: #999;
  font-size: 12px;
  padding: 3px 8px;
}

.pagination {
  margin-top: 30px;
  text-align: center;
}

.tag-edit-container {
  padding: 10px 0;
}

.section-title {
  font-weight: 500;
  margin-bottom: 10px;
  color: #606266;
  font-size: 14px;
}

.section-title::before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 16px;
  background-color: #409eff;
  margin-right: 8px;
  border-radius: 2px;
}

.section-desc {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
  padding-left: 12px;
}

.tags-list {
  min-height: 60px;
  padding: 10px;
  border-radius: 6px;
  border: 1px dashed #e4e7ed;
  transition: all 0.3s;
}

.no-tags {
  color: #909399;
  padding: 10px 0;
}

.tag-input {
  width: 100%;
  margin-bottom: 20px;
}

.tags-list:hover {
  border-color: #c0c4cc;
}

.custom-tag {
  background-color: #f0f7ff;
  color: #409eff;
  border-color: #b3d8ff;
  margin: 8px 8px 8px 0;
  height: 32px;
  line-height: 30px;
  border-radius: 16px;
  font-size: 14px;
}

.custom-tag .el-tag__close {
  color: #409eff;
  background: rgba(64, 158, 255, 0.15);
  border-radius: 50%;
  width: 16px;
  height: 16px;
  line-height: 16px;
  margin-left: 5px;
}

.tag-item-enter-active {
  animation: tagIn 0.3s ease-out;
}

.tag-item-leave-active {
  animation: tagOut 0.2s ease-in;
}

@keyframes tagIn {
  0% {
    opacity: 0;
    transform: scale(0.8);
    margin-top: 0;
    height: 0;
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes tagOut {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  line-height: 1;
}

.empty-text {
  font-size: 16px;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #c0c4cc;
  max-width: 300px;
  margin: 0 auto;
}

/* 按钮样式优化 */
.cancel-btn {
  margin-right: 10px;
}

.save-btn {
  background-color: #409eff;
  border-color: #409eff;
}

.save-btn:hover {
  background-color: #3391e8;
  border-color: #3391e8;
}

/* 区块样式 */
.add-tag-section {
  margin-bottom: 24px;
}

.current-tags-section {
  margin-bottom: 12px;
}

/* 搜索状态样式 */
.search-status {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f0f7ff;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #409eff;
  font-size: 14px;
}

</style>